import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, Subject, take, tap } from "rxjs";
import { UserPreferencesService } from "../../services/user-preferences.service";
import { managePageUserPreferenceStrings } from "../../constants/user-preferences.constants";
import { BudgetDataService } from "../../../dashboard/budget-data/budget-data.service";
import { BudgetTimeframesType } from "../../../shared/types/budget.interface";

interface TimeframeConfig {
  budgetId: number;
  mode: string;
  ids: (string | number)[];
}

@Injectable()
export class TimeframeConfigDialogService {
  private budgetDataService = inject(BudgetDataService);
  private userPreferencesService = inject(UserPreferencesService);
  private _userTableTimeframeConfigPreference$ = new BehaviorSubject<TimeframeConfig>(null);
  public userTableTimeframeConfigPreference$ = this._userTableTimeframeConfigPreference$.asObservable();

  public postUserTimeframeConfig(
    companyId, budgetId, {mode = undefined, timeframeIds = undefined}
  ): Observable<any>  {
    const prevData = this.userTimeframeConfig;
    const personalizationData: TimeframeConfig = {
      budgetId: budgetId,
      mode: mode ? mode : prevData.mode,
      ids: timeframeIds ? timeframeIds : prevData.ids
    }
    const personalizationDataPayload = {
      mode: personalizationData.mode,
      ids: this.convertAllocIdsToRelativeIds(
        personalizationData.ids, personalizationData.mode
      )
    }
    return new Observable((observer) => {
      const budgetTableAreaDetails = managePageUserPreferenceStrings['budget_table']
      this.userPreferencesService.postUserPersonalizationDataConfig(
        companyId,
        budgetId,
        budgetTableAreaDetails.name,
        budgetTableAreaDetails.personalization_keys.timeframe_config,
        personalizationDataPayload
      ).pipe(take(1))
      .subscribe({
        next: (data) => {
          this.userTimeframeConfig = personalizationData;
          observer.next(data);
          observer.complete();
        },
        error: error => {
          observer.error(error);
        }
      });
    });
  }

  public set userTimeframeConfig(config) {
    this._userTableTimeframeConfigPreference$.next(config);
  }

  public get userTimeframeConfig() {
    return this._userTableTimeframeConfigPreference$.getValue();
  }

  private convertAllocIdsToRelativeIds(
    allocIds: (string | number)[], budgetMode: string
  ): (string | number)[] {
    if (allocIds.length === 0) {
      return [];
    }
    const relativeIds = [];
    const timeframes = this.budgetDataService.timeframesSnapshot;
    if (
      (timeframes.length === 12  && budgetMode === BudgetTimeframesType.Month) ||
      (timeframes.length === 4 && budgetMode === BudgetTimeframesType.Quarter)
    ) {
      allocIds.forEach((id) => {
        const index = timeframes.findIndex((timeframe) => timeframe.id === id);
        relativeIds.push(timeframes[index].order);
      });
    } else {
      return allocIds;
    }
    return relativeIds;
  }

  public convertRelativeIdsToAllocIds(relativeIds: number[]): number[] {
    const timeframes = this.budgetDataService.timeframesSnapshot;
    const allocIds = [];
    relativeIds.forEach((orderId) => {
      const index = timeframes.findIndex((timeframe) => timeframe.order === orderId);
      if (index !== -1) {
        allocIds.push(timeframes[index].id);
      }
    });
    return allocIds;
  }
}
